import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { Eye, EyeClosed, Envelope, Lock, CheckCircle } from '@phosphor-icons/react'

import TextField from '../FormLegacy/FormFieldsV2/TextField'
import { getPasswordInfo, renderPasswordCheckers } from '../../helpers/helpers'
import { PASSWORD_SYMBOLS_AT_LEAST } from '../../helpers/constants'
import { formValidation } from '../FormLegacy/validation'
import api from '../../api/api'

import AuthLayout from './AuthLayout'
import { validatePasswordInfo } from './utils'

const ResetPassword = () => {
  const { t } = useTranslation(['auth'])
  const { email, hash } = useParams()
  const [passwordValidation, setPasswordValidation] = React.useState({
    symbolsAtLeast: false,
    upperCase: false,
    lowerCase: false,
    oneNumber: false,
  })
  const [showPassword, setShowPassword] = React.useState(false)
  const [isSuccess, setIsSuccess] = React.useState(false)
  const [error, setError] = React.useState('')

  const validationSchema = Yup.object().shape({
    password: formValidation.password,
    password2: formValidation.password2,
  })

  const navigate = useNavigate()

  const onSubmit = (values) => {
    setError('')

    if (!validatePasswordInfo(passwordValidation)) {
      setError(t('auth:resetPassword.checkRequirements'))
      return
    }

    api
      .updatePassword(values.email, hash, values.password)
      .then(() => {
        setIsSuccess(true)
      })
      .catch(() => {
        setError(t('auth:resetPassword.linkExpired'))
      })
  }

  return (
    <AuthLayout>
      <Formik
        initialValues={{
          email,
          password: '',
          password2: '',
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ handleSubmit, values, handleChange, touched, errors, setFieldTouched }) =>
          isSuccess ? (
            <div className="auth__password-reset-success">
              <CheckCircle size={32} />
              <h2>{t('auth:resetPassword.changed')}</h2>
              <p>{t('auth:resetPassword.changedDescription')}</p>
              <button onClick={() => navigate('/login')} className="button button-primary">
                {t('auth:shared.backToLogin')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth__form">
              <h1>{t('auth:resetPassword.title')}</h1>
              <TextField
                type="email"
                name="email"
                label={t('auth:shared.email')}
                placeholder={t('auth:shared.email')}
                value={values.email}
                leftIcon={<Envelope />}
                // Note: You can't edit email in this step, it's email passed on previous page
                disabled
              />
              <div className="password-wrapper">
                <TextField
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  label={t('auth:shared.password')}
                  placeholder={t('auth:shared.password')}
                  value={values.password}
                  leftIcon={<Lock />}
                  onChange={(e) => {
                    setPasswordValidation(getPasswordInfo(e.target.value))
                    handleChange(e)
                  }}
                  isError={
                    touched.password &&
                    (!!errors.password || !validatePasswordInfo(passwordValidation))
                  }
                  onBlur={() => setFieldTouched('password')}
                  rightIcon={
                    <div className="password-eye" onClick={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? <EyeClosed /> : <Eye />}
                    </div>
                  }
                />
              </div>
              <div className="password__checklist">
                <h3 className="password__title">{t('auth:passwordChecklist.title')}</h3>
                {renderPasswordCheckers([
                  {
                    name: t('auth:passwordChecklist.atLeastLetters', {
                      count: PASSWORD_SYMBOLS_AT_LEAST,
                    }),
                    value: passwordValidation.symbolsAtLeast,
                  },
                  {
                    name: t('auth:passwordChecklist.lowerCase'),
                    value: passwordValidation.lowerCase,
                  },
                  {
                    name: t('auth:passwordChecklist.upperCase'),
                    value: passwordValidation.upperCase,
                  },
                  { name: t('auth:passwordChecklist.number'), value: passwordValidation.oneNumber },
                ])}
              </div>
              <TextField
                type={showPassword ? 'text' : 'password'}
                name="password2"
                label={t('auth:resetPassword.confirmPasswordLabel')}
                placeholder={t('auth:shared.password')}
                value={values.password2}
                leftIcon={<Lock />}
                onChange={handleChange}
                isError={
                  touched.password2 &&
                  (!!errors.password2 || !validatePasswordInfo(passwordValidation))
                }
                onBlur={() => setFieldTouched('password2')}
              />
              {error && <p className="auth__form__description error">{error}</p>}
              <button type="submit" className="button button-primary">
                {t('auth:shared.submit')}
              </button>
            </form>
          )
        }
      </Formik>
    </AuthLayout>
  )
}

export default ResetPassword
